import { Component, computed, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getSilkRemaining, getTowMileageStats } from '@cpr/shared/cpr-math';
import type { IItemsTableOptions } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/data-source/data-source-items/interfaces/items-table-options.interface';
import { FiltersChannel } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/filters/models/filters-channel.model';
import { TableComponent } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/tables/components/table/table.component';
import type { IFilters } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/filters/filters.interface';
import type { ITablePagination } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/table/table-pagination.interface';
import type { ITableSort } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/table/table-sort.interface';
import { SessionService } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/services/session.service';
import { map, Observable } from 'rxjs';
import type { ICuttingPoint } from '../../interface/cutting-point.interface';
import type { TowRouteData } from '../../interface/tow-route-data.interface';
import { CuttingPointService } from '../../service/cutting-point.service';

@Component({
  template: `
    <app-header>
      <cpr-printout-menu />
    </app-header>

    @if (routeData.tow(); as tow) {
      <mat-card style="margin: 0 var(--global-padding) var(--global-padding)">
        <mat-card-header>
          <mat-card-title>Cutting points</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <table mat-table [dataSource]="dataSource$" matSort matSortDisableClear>
            <ng-container matColumnDef="sectionId">
              <th mat-header-cell *matHeaderCellDef>
                @if (!isLoading) {
                  <span>Index</span>
                }
              </th>
              <td mat-cell *matCellDef="let item">{{ item.sectionId }}</td>
            </ng-container>

            <ng-container matColumnDef="cuttingPoint">
              <th mat-header-cell *matHeaderCellDef>
                @if (!isLoading) {
                  <span>Cutting point</span>
                }
              </th>
              <td mat-cell *matCellDef="let item">{{ item.cuttingPoint }}</td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="headings"></tr>
            <tr
              mat-row
              class="row"
              [class.hidden]="isLoading"
              *matRowDef="let row; columns: headings"
            ></tr>

            <tr class="mat-row" *matNoDataRow [hidden]="isLoading">
              <td class="mat-cell" [attr.colspan]="headings.length">
                <span>No matching data.</span>
              </td>
            </tr>
          </table>
        </mat-card-content>
      </mat-card>

      <mat-card style="margin: var(--global-padding)">
        <mat-card-content class="info-box">
          <span>
            Factor:
            {{ tow.metadata.factor ?? 'N/A' }}
          </span>

          <span>
            Miles Per Division:
            {{ (towMileageStats().milesPerDivision | number: '1.0-2') || 'N/A' }}
          </span>

          <span>
            Divisions per Block:
            {{ towMileageStats().milesPerDivision | divisionsPerBlock }}
          </span>

          <span>
            Silk Remaining:
            {{ (silkRemaining | async | number: '1.0-2') || 'N/A' }}
          </span>
        </mat-card-content>
      </mat-card>
    }
  `,
  styles: `
    .info-box {
      display: flex;
      flex-flow: row wrap;
      gap: 28px;
    }
  `,
})
export class TowCuttingPointsScreenComponent
  extends TableComponent<ICuttingPoint, string>
  implements OnInit
{
  readonly headings = ['sectionId', 'cuttingPoint'];
  readonly widths = [50, 50];

  @Input() filtersChannel?: FiltersChannel;
  @Input() options: IItemsTableOptions;
  @Input() dontCache = false;
  @Input() tableAlias: string = 'cutting-points';

  readonly routeData = inject(ActivatedRoute).snapshot.data as TowRouteData;

  readonly towMileageStats = computed(() =>
    getTowMileageStats({
      tow: this.routeData.tow()?.metadata,
      towLog: this.routeData.towLog(),
    }),
  );

  silkRemaining!: Observable<number>;

  constructor(
    sessionService: SessionService,
    private readonly cuttingPoints: CuttingPointService,
  ) {
    super(sessionService);
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.silkRemaining = this.dataSource$.pipe(
      map((cuttingPoints) =>
        getSilkRemaining(cuttingPoints, this.towMileageStats().milesPerDivision),
      ),
    );
  }

  protected async getPaginated(
    filters: IFilters,
    sort: ITableSort,
    _pagination: ITablePagination,
    search: string,
  ) {
    const result = await this.cuttingPoints.getPaginated('', {
      filters,
      sort,
      pagination: { pageIndex: 0, pageSize: 999 },
      search,
      simpleFilters: { towId: `ObjectId:${this.routeData.tow()._id}` },
    });
    result.data.sort((a, b) => a.sectionId - b.sectionId);
    return result;
  }
}
